import {
  Box,
  Center,
  HStack,
  Icon,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  CornersIn,
  CornersOut,
  Pause,
  Play,
  SpeakerSimpleHigh,
  SpeakerSimpleSlash,
} from "@phosphor-icons/react";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/lazy";
import formatDurationShort from "../../lib/FormatDurationShort";
import { Silabus } from "../../const/types";
import cr from "../../lib/request";

type Props = {
  url: string;
  data: Silabus;
  autoPlay?: boolean;
};

export default function VideoPlayer({ url, data, autoPlay }: Props) {
  useEffect(() => {
    const sliderElement = document.querySelector(
      ".slider-element"
    ) as HTMLElement;

    if (sliderElement) {
      const handleKeyPress: EventListener = (event) => {
        if ((event as KeyboardEvent).key === " ") {
          event.preventDefault();
          setPlaying((prevPlaying) => !prevPlaying);
        }
      };
      sliderElement.addEventListener("keydown", handleKeyPress);
      return () => {
        sliderElement.removeEventListener("keydown", handleKeyPress);
      };
    }
  }, []);

  const [progressTime, setProgressTime] = useState<number>(0);
  const [progressTimeLength, setProgressTimeLength] = useState<number>(0);
  const [playing, setPlaying] = useState<boolean>(autoPlay || false);
  const [muted, setMuted] = useState<boolean>(false);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);
  const [played, setPlayed] = useState<number>(0);
  const playerRef = useRef<ReactPlayer | null>(null);
  const [isCursorActive, setIsCursorActive] = useState(true);
  const cursorTimerRef = useRef<any | null>(null);
  const [isRendered, setIsRendered] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsRendered(true);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!playing) {
      clearTimeout(cursorTimerRef.current);
      setIsCursorActive(true);
    } else {
      cursorTimerRef.current = window.setTimeout(() => {
        setIsCursorActive(false);
      }, 3000);
    }

    const handleMouseMove = (event: MouseEvent) => {
      const videoContainer = document.querySelector("#playOverlay");
      if (videoContainer) {
        const rect = videoContainer.getBoundingClientRect();
        const isInVideoContainer =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!playing) {
          clearTimeout(cursorTimerRef.current);
          setIsCursorActive(true);
        } else if (isInVideoContainer) {
          clearTimeout(cursorTimerRef.current);
          setIsCursorActive(true);

          cursorTimerRef.current = window.setTimeout(() => {
            setIsCursorActive(false);
          }, 3000);
        } else {
          if (isRendered) {
            setIsCursorActive(false);
          }
        }
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);

      if (cursorTimerRef.current) {
        clearTimeout(cursorTimerRef.current);
      }
    };
  }, [playing, isRendered]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      // Periksa apakah sekarang dalam mode fullscreen
      const isFullscreen = document.fullscreenElement !== null;
      setFullscreen(isFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Fungsi untuk masuk ke mode fullscreen
  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
      setFullscreen(true);
    }
  };

  // Fungsi untuk keluar dari mode fullscreen
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  // Fungsi untuk menangani double-click fullscreen
  const handleDoubleClickFullscreen = () => {
    const isFullscreen = document.fullscreenElement !== null;
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  const handleVolume = (value: number) => {
    clearTimeout(cursorTimerRef.current);
    setIsCursorActive(true);
    setVolume(value);
  };

  const handleSeek = (value: number) => {
    clearTimeout(cursorTimerRef.current);
    setIsCursorActive(true);
    setPlayed(value);
    if (playerRef.current) {
      playerRef.current.seekTo(value, "fraction");
    }
  };

  useEffect(() => {
    if (progressTimeLength > 0) {
      if (data.id) {
        const url = `api/subcourse/${data.id}/updatevideoduration`;
        const payload = {
          duration: progressTimeLength,
        };
        cr.post(url, payload)
          .then((r) => {
            if (r.status === 200) {
            }
          })
          .catch((e) => {
            console.log(e);
          });
      }
    }
  }, [progressTimeLength, data.id]);

  const wrapperStyle: React.CSSProperties = {
    position: fullscreen ? "fixed" : "relative",
    top: 0,
    left: 0,
    width: fullscreen ? "100vw" : "100%",
    height: fullscreen ? "100vh" : "auto",
  };

  return (
    <VStack
      zIndex={4}
      position={"relative"}
      align={"stretch"}
      overflow={"clip"}
      role="group"
      cursor={isCursorActive ? "auto" : "none !important"}
      style={wrapperStyle}
    >
      <div className="player-wrapper" style={wrapperStyle}>
        <ReactPlayer
          controls={false}
          loop={true}
          ref={playerRef}
          playing={playing}
          muted={muted}
          className="react-player"
          url={url}
          width="100%"
          height="100%"
          onPlay={() => {
            setPlaying(true);
          }}
          onPause={() => {
            setPlaying(false);
          }}
          onProgress={(progress) => {
            setPlayed(progress.played);
            setProgressTime(progress.playedSeconds);
          }}
          onDuration={(progress) => {
            setProgressTimeLength(progress);
          }}
          volume={volume}
        />
      </div>

      <VStack
        zIndex={6}
        w={"100%"}
        position={"absolute"}
        top={0}
        transition={"200ms"}
        transform={isCursorActive ? "translateY(0px)" : "translateY(-100px)"}
        p={1}
        align={"flex-start"}
        color={"white"}
        gap={0}
        cursor={isCursorActive ? "auto" : "none !important"}
      >
        <VStack
          bg={"blackAlpha.500"}
          backdropFilter={"blur(20px)"}
          p={3}
          gap={0}
          w={"100%"}
          align={"flex-start"}
          borderRadius={8}
        >
          <Tooltip label={data.nama_sub_course} mb={2}>
            <Text fontWeight={600} noOfLines={1}>
              {data.nama_sub_course}
            </Text>
          </Tooltip>

          <Text opacity={0.5} fontSize={13}>
            {data.course_name}
          </Text>
        </VStack>
      </VStack>

      {/* play button overlay */}
      <VStack
        id="playOverlay"
        zIndex={5}
        position={"absolute"}
        justify={"center"}
        w={"100%"}
        h={"100%"}
        onClick={() => {
          setPlaying(!playing);
        }}
        onDoubleClick={handleDoubleClickFullscreen}
        cursor={isCursorActive ? "auto" : "none !important"}
        // bgImage={!playing ? "red" : "" }
      >
        {!playing && (
          <Center bg={"blackAlpha.500"} borderRadius={"full"} p={3}>
            <Icon
              as={playing ? Pause : Play}
              fontSize={[24, null, 32]}
              weight="fill"
              color={"white"}
            />
          </Center>
        )}
      </VStack>

      {/* controls */}
      <HStack
        zIndex={6}
        w={"100%"}
        p={1}
        position={"absolute"}
        bottom={0}
        transform={isCursorActive ? "translateY(0px)" : "translateY(100px)"}
        transition={"200ms"}
      >
        <VStack
          align={"stretch"}
          w={"100%"}
          gap={0}
          borderRadius={8}
          p={1}
          bg={"blackAlpha.500"}
          backdropFilter={"blur(20px)"}
        >
          {/* played slider */}
          <HStack mb={-1}>
            <VStack w={"100%"} px={2}>
              <Box w={"100%"} role="group">
                <Slider
                  colorScheme="ap"
                  aria-label="seek"
                  value={played}
                  max={1}
                  min={0}
                  step={0.01}
                  onChange={handleSeek}
                  position={"absolute"}
                  w={"100%"}
                  zIndex={3}
                  transition={"0ms"}
                  className="slider-element"
                  tabIndex={0}
                  size={"sm"}
                >
                  <SliderTrack bg={"var(--divider3)"}>
                    <SliderFilledTrack bg={"white"} />
                  </SliderTrack>

                  <SliderThumb
                    transform={"scale(0)"}
                    _groupHover={{ transform: "scale(1), translateY(50%)" }}
                    transition={"0ms !important"}
                    _focusVisible={{ boxShadow: "none" }}
                  />
                </Slider>
              </Box>
            </VStack>

            <HStack color={"white"} gap={1} mr={1}>
              <Text w={["45px", null, "55px"]} fontSize={[10, null, 12]}>
                {formatDurationShort(progressTime)}
              </Text>

              <Text fontSize={[10, null, 12]}>/</Text>

              <Text w={["45px", null, "55px"]} fontSize={[10, null, 12]} ml={1}>
                {formatDurationShort(progressTimeLength)}
              </Text>
            </HStack>
          </HStack>

          {/* bottom controls */}
          <HStack justify={"space-between"}>
            {/* left */}
            <HStack gap={1}>
              <Tooltip label={!playing ? "Play" : "Pause"}>
                <IconButton
                  aria-label="play button"
                  color={"white"}
                  icon={
                    <Icon
                      as={playing ? Pause : Play}
                      fontSize={16}
                      weight="fill"
                    />
                  }
                  className="btn"
                  onClick={() => {
                    setPlaying(!playing);
                  }}
                  size={"xs"}
                  ml={1}
                />
              </Tooltip>

              <HStack
                _hover={{ bg: "var(--divider2)" }}
                borderRadius={8}
                pr={4}
                h={"24px"}
                gap={0}
                role="group"
              >
                <Tooltip label={!muted ? "Mute" : "Unmute"} ml={-1}>
                  <IconButton
                    aria-label="mute button"
                    icon={
                      <Icon
                        as={muted ? SpeakerSimpleSlash : SpeakerSimpleHigh}
                        fontSize={16}
                        weight="fill"
                      />
                    }
                    className="btn-clear"
                    color={"white"}
                    onClick={() => {
                      setMuted(!muted);
                    }}
                  />
                </Tooltip>

                <Slider
                  aria-label="seek"
                  value={volume}
                  max={1}
                  min={0}
                  step={0.01}
                  onChange={handleVolume}
                  position={"absolute"}
                  w={"50px"}
                  size={"sm"}
                >
                  <SliderTrack bg={"var(--divider3)"}>
                    <SliderFilledTrack bg={"white"} />
                  </SliderTrack>

                  <SliderThumb
                    transform={"scale(0)"}
                    _groupHover={{ transform: "scale(1), translateY(50%)" }}
                    transition={"0ms !important"}
                    _focusVisible={{ boxShadow: "none" }}
                  />
                </Slider>
              </HStack>
            </HStack>

            {/* right */}
            <HStack>
              <Tooltip label={!fullscreen ? "Full screen" : "Exit full screen"}>
                <IconButton
                  aria-label="fullscreen button"
                  icon={
                    <Icon
                      as={!fullscreen ? CornersOut : CornersIn}
                      fontSize={16}
                    />
                  }
                  className="btn"
                  size={"xs"}
                  color={"white"}
                  mr={1}
                  onClick={fullscreen ? exitFullscreen : enterFullscreen}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </VStack>
      </HStack>
    </VStack>
  );
}
